import React, { useEffect, useState } from "react";
import { Input, DatePicker, Button, Skeleton, message } from "antd";
import moment from "moment";
import "./filterPanels.css";

/**
 * Global filters on the board view - parameters sent back to the resource's API. Apply
 * hands the resource up to the board, which re-requests with these values in the query
 * string or body depending on each filter's parameterType. Unlike data filters, these
 * change what the server returns, so row counts and totals change with them.
 *
 * Replaces CustomFilters.js, which is kept for revert. What is new: the `config` bounds
 * the editor saves (min / max / maxLength / date range) are actually enforced here,
 * values are validated before the request goes out, and the panel says something when a
 * resource has no global filters instead of rendering blank.
 */

const resourceKey = (resource, index) => resource?.connectionData?.id ?? resource?.id ?? index;

const hasValue = (v) => v !== null && v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0);

/**
 * Earliest date the picker will allow, from the filter's saved config.
 *
 * A "dynamic" range is a rolling window ending today; a fixed range is a real date.
 * The old version read config.range where it meant config.value in the Last Week
 * branch, so Last Week silently fell through to the year-2000 floor.
 */
export function getMinDate(config) {
  if (!config) return moment("2000-01-01");

  if (config.range && config.range !== "dynamic") return moment(config.value);

  switch (config.value) {
    case "LastWeek":
      return moment().utc().subtract(7, "d");
    case "Last2Weeks":
      return moment().utc().subtract(14, "d");
    case "LastMonth":
      return moment().utc().subtract(1, "months");
    case "LastQuarter":
      return moment().utc().subtract(3, "months");
    case "Last6Months":
      return moment().utc().subtract(6, "months");
    case "LastYear":
      return moment().utc().subtract(1, "years");
    default:
      return moment("2000-01-01");
  }
}

/** Whatever is wrong with this filter's value, as a message or null. */
function validateFilter(filter) {
  if (!hasValue(filter.value)) return null;

  const config = filter.config || {};

  if (filter.dataType === "number") {
    const n = Number(filter.value);
    if (Number.isNaN(n)) return "Enter a number.";
    /* The editor's Limit convention: a row cap of 0 or less returns nothing, which
       reads as a broken report rather than an empty filter. */
    if (String(filter.parameterName).toLowerCase() === "limit" && n <= 0) return "Limit must be greater than 0.";
    if (config.min !== null && config.min !== undefined && n < Number(config.min)) return `Minimum is ${config.min}.`;
    if (config.max !== null && config.max !== undefined && n > Number(config.max)) return `Maximum is ${config.max}.`;
  }

  if (filter.dataType === "text" && config.maxLength && String(filter.value).length > Number(config.maxLength)) {
    return `Maximum ${config.maxLength} characters.`;
  }

  if (filter.dataType === "numArray" && filter.value.some((v) => Number.isNaN(Number(v)))) {
    return "Enter numbers separated by commas.";
  }

  return null;
}

export default function GlobalFiltersPanel({ selectedBoard, onApplyFilter }) {
  const [resources, setResources] = useState(null);

  useEffect(() => {
    // Was initial state only, so switching boards kept the previous board's filters.
    setResources(selectedBoard?.resources || null);
  }, [selectedBoard]);

  if (!selectedBoard) return <Skeleton />;
  if (!resources) return null;

  const setFilterValue = (resource, filter, value) => {
    setResources((current) =>
      current.map((r) =>
        resourceKey(r) !== resourceKey(resource)
          ? r
          : {
              ...r,
              customFilters: (r.customFilters || []).map((f) => (f.id === filter.id ? { ...f, value } : f)),
            },
      ),
    );
  };

  const clearResourceFilters = (resource) => {
    const cleared = { ...resource, customFilters: (resource.customFilters || []).map((f) => ({ ...f, value: null })) };
    setResources((current) => current.map((r) => (resourceKey(r) === resourceKey(resource) ? cleared : r)));
    onApplyFilter(cleared);
  };

  const applyResourceFilters = (resource) => {
    const errors = (resource.customFilters || []).map(validateFilter).filter(Boolean);
    if (errors.length > 0) {
      message.error(errors[0]);
      return;
    }

    if (!(resource.customFilters || []).some((f) => hasValue(f.value))) {
      message.info("Enter a value in at least one filter first.");
      return;
    }

    onApplyFilter(resource);
  };

  const renderInput = (resource, filter) => {
    const onChange = (value) => setFilterValue(resource, filter, value);
    const config = filter.config || {};

    switch (filter.dataType) {
      case "number":
        return (
          <Input
            type="number"
            value={filter.value ?? ""}
            min={config.min}
            max={config.max}
            placeholder={
              config.min !== undefined && config.max !== undefined ? `${config.min} - ${config.max}` : "Value"
            }
            onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
          />
        );

      case "date":
        return (
          <DatePicker
            /* Was moment(value) with no guard, so an empty date filter showed today and
               looked as though it were already set. */
            value={filter.value ? moment(filter.value) : null}
            disabledDate={(current) => {
              if (!current) return false;
              const min = getMinDate(config);
              return current > moment().endOf("day") || current < min;
            }}
            onChange={(date) => onChange(date ? date.format("YYYY-MM-DDTHH:mm:ss.SSSZ") : null)}
          />
        );

      case "strArray":
        return (
          <Input
            value={Array.isArray(filter.value) ? filter.value.join(",") : (filter.value ?? "")}
            placeholder="value1, value2"
            onChange={(e) =>
              onChange(
                e.target.value
                  .split(",")
                  .map((v) => v.trim())
                  .filter((v) => v !== ""),
              )
            }
          />
        );

      case "numArray":
        return (
          <Input
            value={Array.isArray(filter.value) ? filter.value.join(",") : (filter.value ?? "")}
            placeholder="1, 2, 3"
            /* The old handler read `.split(",").filter(...)).map(...)` - the map ran on
               the return of the update call, not on the array, and threw on every
               keystroke. */
            onChange={(e) =>
              onChange(
                e.target.value
                  .split(",")
                  .map((v) => v.trim())
                  .filter((v) => v !== "")
                  .map((v) => Number(v)),
              )
            }
          />
        );

      default:
        return (
          <Input
            value={filter.value ?? ""}
            maxLength={config.maxLength ? Number(config.maxLength) : undefined}
            placeholder="Value"
            onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
          />
        );
    }
  };

  return (
    <div className="vf-panel">
      {resources.map((resource, i) => {
        const filters = resource.customFilters || [];
        const activeCount = filters.filter((f) => hasValue(f.value)).length;

        return (
          <div className="vf-resource" key={`vf_global_${resourceKey(resource, i)}`}>
            <div className="vf-resource-head">
              <span className="vf-resource-name">{resource.connectionData?.name || `Resource ${i + 1}`}</span>
              {activeCount > 0 ? (
                <span className="vf-active-count">
                  {activeCount} active {activeCount === 1 ? "filter" : "filters"}
                </span>
              ) : null}
            </div>

            {filters.length === 0 ? (
              <div className="vf-empty">
                <span className="vf-empty-title">No global filters</span>
                Add them in the editor's Filters section to send parameters to this resource's API.
              </div>
            ) : (
              <>
                <div className="vf-fields">
                  {filters.map((filter) => {
                    const error = validateFilter(filter);

                    return (
                      <div className="vf-field" key={`vf_gf_${filter.id}`}>
                        <span className="vf-field-label">
                          {filter.parameterName}
                          <span className="vf-op">{filter.parameterType === "query" ? "Query" : "Body"}</span>
                        </span>
                        {renderInput(resource, filter)}
                        {error ? <span className="vf-error">{error}</span> : null}
                      </div>
                    );
                  })}
                </div>

                <div className="vf-actions">
                  <Button type="primary" onClick={() => applyResourceFilters(resource)}>
                    Apply
                  </Button>
                  <Button onClick={() => clearResourceFilters(resource)} disabled={activeCount === 0}>
                    Reset
                  </Button>
                  {/* Says out loud what the two tabs actually do differently. */}
                  <span className="vf-hint">Re-requests data from the source.</span>
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}